/**
 * Análisis del archivo de Ventas de Libros Digitales: detección de columnas, validación
 * local, deduplicación por código y agrupación de los códigos de un mismo alumno.
 *
 * Sin React ni antd a propósito, igual que la plantilla: todo esto se puede probar con un
 * `node script.js` sin levantar la app.
 *
 * Lo que NO decide aquí: si el código sigue vivo y de quién es la venta que se lo llevó.
 * Eso lo resuelve el backend (`ventasLibrosDigitales/resolverLotesVentasLibrosDigitales`) y
 * llega ya resuelto en `veredictosPorFila`, para que la vista previa no pueda discrepar de
 * lo que realmente va a hacer la importación.
 */

import { normalizarTexto } from "../ImportarVentasLibros/coincidenciaMaterial";
import { COLUMNAS_ESPERADAS, COLUMNAS_OBLIGATORIAS } from "./plantillaVentasLibrosDigitales";

/**
 * Los 10 desenlaces posibles de una fila, con su etiqueta y su color. Es el contrato que
 * comparten la vista previa, el modal de resultados y el Excel que se descarga: si aquí se
 * agrega uno, aparece en los tres lados.
 *
 * `escribe` distingue los que tocan la base de datos de los que sólo se reportan.
 */
export const ESTADOS = {
  creada: { etiqueta: "Crear venta", color: "green", escribe: true },
  reasignada: { etiqueta: "Reasignar cliente", color: "blue", escribe: true },
  separada: { etiqueta: "Separar en venta propia", color: "geekblue", escribe: true },
  yaAsignada: { etiqueta: "Ya asignada, sólo correo", color: "cyan", escribe: false },
  agrupada: { etiqueta: "Se agrupa con otra fila", color: "purple", escribe: false },
  otroCliente: { etiqueta: "Es de otro cliente", color: "orange", escribe: false },
  timbrada: { etiqueta: "Timbrada, no se toca", color: "volcano", escribe: false },
  enGlobal: { etiqueta: "En Factura Global", color: "volcano", escribe: false },
  error: { etiqueta: "Revisar", color: "red", escribe: false },
  //Sólo en los resultados: la importación se detuvo antes de llegar a esa fila.
  detenida: { etiqueta: "No se procesó (detenida)", color: "default", escribe: false },
};

export const etiquetaEstado = (estado) => ESTADOS[estado]?.etiqueta || estado || "";
export const colorEstado = (estado) => ESTADOS[estado]?.color || "default";

//Los códigos se comparan sin distinguir mayúsculas ni espacios de sobra: se capturan a mano
//y "lv-2026-871" y "LV-2026-871 " son el mismo código. Es la misma llave que usa el backend.
export const llaveLote = (valor) =>
  String(valor === undefined || valor === null ? "" : valor)
    .trim()
    .toUpperCase();

/**
 * Encuentra, para cada columna esperada, el encabezado real del archivo que empieza con su
 * prefijo. Devuelve también las obligatorias que no aparecieron.
 */
export const detectarColumnas = (filas) => {
  const llaves = new Set();
  (filas || []).forEach((fila) => {
    Object.keys(fila).forEach((llave) => {
      if (!llave.startsWith("__")) llaves.add(llave);
    });
  });

  const columnas = {};
  COLUMNAS_ESPERADAS.forEach((esperada) => {
    const encontrada = [...llaves].find((llave) =>
      normalizarTexto(llave).startsWith(esperada.prefijo),
    );
    if (encontrada) columnas[esperada.clave] = encontrada;
  });

  const faltantes = COLUMNAS_OBLIGATORIAS.filter((clave) => !columnas[clave]);

  return {
    columnas,
    faltantes,
    etiquetasFaltantes: COLUMNAS_ESPERADAS.filter((c) => faltantes.includes(c.clave))
      .map((c) => c.etiqueta)
      .join(", "),
  };
};

const leer = (fila, columna) => String(fila?.[columna] ?? "").trim();

/**
 * Datos crudos de cada renglón del archivo, ya con el alumno resuelto. Se hace en una pasada
 * aparte porque el backend necesita los pares (código, alumno) ANTES de poder dar su
 * veredicto, y el veredicto es lo que decide qué va a pasar con la fila.
 */
export const lecturaDeFilas = ({ filas, columnas, indices, buscarAlumno }) =>
  (filas || []).map((fila, indice) => {
    const numeroFila = (fila["__rowNum__"] ?? indice) + 1;
    const matricula = leer(fila, columnas.matricula);
    const numeroLote = leer(fila, columnas.lote);
    const correo = leer(fila, columnas.correo);
    const material = leer(fila, columnas.material);

    const nombreExcel = [
      fila?.[columnas.nombre],
      fila?.[columnas.apellidoPaterno],
      fila?.[columnas.apellidoMaterno],
    ]
      .map((parte) => String(parte ?? "").trim())
      .filter(Boolean)
      .join(" ");

    const { alumno, criterio } = matricula
      ? buscarAlumno(matricula, correo, indices)
      : { alumno: null, criterio: "noEncontrado" };

    return { indice, numeroFila, matricula, numeroLote, correo, material, nombreExcel, alumno, criterio };
  });

/**
 * Convierte la lectura del archivo más el veredicto del backend en lo que el importador
 * genérico espera por fila: `{ok, accion, datos}`.
 *
 * Reglas que sólo se pueden aplicar viendo el archivo COMPLETO, y que por eso viven aquí y
 * no en `validarFila` (que corre fila por fila):
 *
 *  - **Deduplicación por código.** Dos filas con el mismo código y distinta matrícula harían
 *    que la segunda reasignara la venta de la primera, borrando la primera asignación sin
 *    dejar rastro. La segunda se reporta y no se procesa.
 *  - **Agrupación por alumno.** Los códigos vivos de un mismo alumno se juntan en UNA venta:
 *    la primera fila del grupo la crea con todos ellos y las demás se marcan como agrupadas.
 *    Hacerlo aquí y no en el backend también evita que dos filas hermanas caigan en bloques
 *    distintos del envío por lotes.
 *  - **Una venta por matrícula.** Los códigos que ya se vendieron se agrupan por venta y,
 *    dentro de cada venta, por alumno. Si la venta trae SÓLO los códigos de un alumno, se le
 *    reasigna completa; si trae más, los de cada alumno se separan a una venta propia. Cuando
 *    los alumnos del archivo cubren la venta entera, el último en procesarse se queda con
 *    ella (para entonces los demás ya se separaron). Es la misma regla que aplica la
 *    importación sobre la venta releída (`ventaEsSoloDelAlumno` en el backend).
 *
 * @param {Array} lecturas            salida de `lecturaDeFilas`
 * @param {Array} veredictos          posicional respecto a `lecturas`; lo devuelve el backend
 * @param {string} bloqueado          si viene, todas las filas salen con ese motivo
 * @returns {Map<number, Object>}     índice de fila -> {ok, accion, datos}
 */
export const analizarArchivo = ({ lecturas, veredictos, bloqueado }) => {
  const porIndice = new Map();

  const error = (lectura, motivo) => ({
    ok: true,
    accion: "error",
    datos: {
      numeroFila: lectura.numeroFila,
      matricula: lectura.matricula,
      numeroLote: lectura.numeroLote,
      correo: lectura.correo,
      material: lectura.material,
      alumno: lectura.alumno ? nombreDeAlumno(lectura.alumno) : lectura.nombreExcel,
      estadoPrevisto: "error",
      motivo,
    },
  });

  const lotesVistos = new Map();
  const preliminares = [];

  lecturas.forEach((lectura, posicion) => {
    if (bloqueado) {
      porIndice.set(lectura.indice, error(lectura, bloqueado));
      return;
    }

    if (!lectura.matricula) {
      porIndice.set(lectura.indice, error(lectura, "El número de control está vacío"));
      return;
    }

    if (!lectura.numeroLote) {
      porIndice.set(lectura.indice, error(lectura, "El número de lote está vacío"));
      return;
    }

    if (!lectura.alumno) {
      porIndice.set(
        lectura.indice,
        error(
          lectura,
          lectura.criterio === "ambiguo"
            ? `"${lectura.matricula}" coincide con más de un alumno; corrige la matrícula en el archivo`
            : `"${lectura.matricula}" no corresponde a ningún alumno registrado`,
        ),
      );
      return;
    }

    const llave = llaveLote(lectura.numeroLote);
    const repetida = lotesVistos.get(llave);

    if (repetida !== undefined) {
      porIndice.set(
        lectura.indice,
        error(lectura, `El código "${lectura.numeroLote}" ya viene en la fila ${repetida}`),
      );
      return;
    }
    lotesVistos.set(llave, lectura.numeroFila);

    const veredicto = veredictos?.[posicion];

    if (!veredicto) {
      porIndice.set(
        lectura.indice,
        error(lectura, "No se pudo consultar el estado de este código; vuelve a subir el archivo"),
      );
      return;
    }

    preliminares.push({ lectura, veredicto });
  });

  //Agrupación de los códigos vivos: de los de un mismo alumno, la PRIMERA fila crea la venta
  //y carga los códigos de todas sus hermanas.
  const lideresPorAlumno = new Map();

  preliminares.forEach(({ lectura, veredicto }) => {
    if (veredicto.estado !== "creada") return;
    const llaveAlumno = String(lectura.alumno._id);
    if (!lideresPorAlumno.has(llaveAlumno)) {
      lideresPorAlumno.set(llaveAlumno, { lider: lectura, lotes: [] });
    }
    lideresPorAlumno.get(llaveAlumno).lotes.push({
      numero: lectura.numeroLote,
      numeroFila: lectura.numeroFila,
      entradaId: veredicto.entradaId || null,
    });
  });

  //Agrupación de los códigos ya vendidos: por venta y, dentro de ella, por alumno.
  const ventasDeOrigen = new Map();

  preliminares.forEach(({ lectura, veredicto }) => {
    if (!["reasignada", "yaAsignada"].includes(veredicto.estado) || !veredicto.ventaId) return;
    const llaveVenta = String(veredicto.ventaId);
    if (!ventasDeOrigen.has(llaveVenta)) {
      ventasDeOrigen.set(llaveVenta, {
        unidadesVenta: Number(veredicto.unidadesVenta || 0),
        grupos: new Map(),
      });
    }
    const grupos = ventasDeOrigen.get(llaveVenta).grupos;
    const llaveAlumno = String(lectura.alumno._id);
    if (!grupos.has(llaveAlumno)) {
      grupos.set(llaveAlumno, {
        lider: lectura,
        estadoVeredicto: veredicto.estado,
        lotes: [],
        unidades: 0,
      });
    }
    const grupo = grupos.get(llaveAlumno);
    grupo.lotes.push({
      numero: lectura.numeroLote,
      numeroFila: lectura.numeroFila,
      entradaId: veredicto.entradaId || null,
    });
    grupo.unidades += Number(veredicto.unidadesCodigo) > 0 ? Number(veredicto.unidadesCodigo) : 1;
  });

  //Qué le pasa a cada alumno en cada venta. Una venta que ya es del alumno ("yaAsignada")
  //no se decide: sólo se le reenvía el correo.
  ventasDeOrigen.forEach(({ unidadesVenta, grupos }) => {
    const reasignables = [...grupos.values()].filter(
      (valueGrupo) => valueGrupo.estadoVeredicto === "reasignada",
    );
    const unidadesArchivo = reasignables.reduce((acc, valueGrupo) => acc + valueGrupo.unidades, 0);
    const cubreLaVenta = unidadesVenta > 0 && unidadesArchivo >= unidadesVenta - 0.0000001;

    //Las filas se procesan en el orden del archivo: el grupo cuya fila líder va al último
    //encuentra la venta ya sólo con lo suyo.
    const seQuedaLaVenta = cubreLaVenta
      ? reasignables.reduce(
          (ultimo, valueGrupo) =>
            !ultimo || valueGrupo.lider.indice > ultimo.lider.indice ? valueGrupo : ultimo,
          null,
        )
      : null;

    reasignables.forEach((valueGrupo) => {
      if (valueGrupo === seQuedaLaVenta) {
        valueGrupo.estadoPrevisto = "reasignada";
        valueGrupo.motivoPrevisto =
          reasignables.length > 1
            ? "Los demás alumnos de esta venta se separan primero; la venta se le queda completa a este alumno"
            : "";
      } else {
        valueGrupo.estadoPrevisto = "separada";
        valueGrupo.motivoPrevisto =
          "La venta trae códigos de otros alumnos (o de nadie del archivo): sus códigos se separan a una venta propia";
      }
    });
  });

  preliminares.forEach(({ lectura, veredicto }) => {
    const llaveAlumno = String(lectura.alumno._id);
    const grupoVivos = veredicto.estado === "creada" ? lideresPorAlumno.get(llaveAlumno) : null;
    const grupoVenta =
      ["reasignada", "yaAsignada"].includes(veredicto.estado) && veredicto.ventaId
        ? ventasDeOrigen.get(String(veredicto.ventaId))?.grupos.get(llaveAlumno)
        : null;
    const grupo = grupoVivos || grupoVenta;
    const esLider = grupo && grupo.lider.indice === lectura.indice;
    const esSeguidora = grupo && !esLider;

    const estadoLider = grupoVenta?.estadoPrevisto || veredicto.estado;
    const motivoLider = grupoVenta?.motivoPrevisto || veredicto.motivo || "";
    const motivoSeguidora = grupoVenta
      ? `Su código va en la misma operación que la fila ${grupo?.lider.numeroFila}`
      : `Su código se agrega a la venta de la fila ${grupo?.lider.numeroFila}`;

    const datos = {
      numeroFila: lectura.numeroFila,
      matricula: lectura.matricula,
      numeroLote: lectura.numeroLote,
      correo: lectura.correo,
      material: lectura.material,
      clienteId: String(lectura.alumno._id),
      alumno: nombreDeAlumno(lectura.alumno),
      insumo: veredicto.insumoDescripcion || "",
      folio: veredicto.folio || "",
      clienteVenta: veredicto.clienteVenta || "",
      rfcVenta: veredicto.rfcVenta || "",
      uuid: veredicto.uuid || "",
      estadoPrevisto: esSeguidora ? "agrupada" : estadoLider,
      motivo: esSeguidora ? motivoSeguidora : motivoLider,
      destinatarios: destinatariosDe(lectura.correo, lectura.alumno).join(", "),
      correoNuevo: esCorreoNuevo(lectura.correo, lectura.alumno),
      //El lote que resolvió la vista previa. La importación ya NO vuelve a buscar el código
      //por número (sin índice, leía todo el historial en cada bloque): lo confirma por este id.
      entradaId: veredicto.entradaId || null,
      ventaId: veredicto.ventaId ? String(veredicto.ventaId) : null,
    };

    const lotesDelGrupo = () =>
      grupo.lotes.map((valueLote) => ({ numero: valueLote.numero, entradaId: valueLote.entradaId }));

    if (esLider && grupoVivos && grupo.lotes.length > 1) {
      datos.lotesVivos = lotesDelGrupo();
      datos.librosAgrupados = grupo.lotes.length;
    }

    //El líder de una venta lleva SIEMPRE sus códigos de esa venta, aunque sea uno: con ellos
    //el backend sabe qué separar si la venta trae más.
    if (esLider && grupoVenta) {
      datos.lotesVenta = lotesDelGrupo();
      if (grupo.lotes.length > 1) datos.librosAgrupados = grupo.lotes.length;
    }

    if (esSeguidora) {
      datos.agrupadaCon = grupo.lider.numeroFila;
    }

    porIndice.set(lectura.indice, { ok: true, accion: "crear", datos });
  });

  return porIndice;
};

//Mismo criterio de nombre que usa el resto del ERP: la bandera `persona` manda, no la simple
//presencia de razonSocial.
export const nombreDeAlumno = (alumno) => {
  const razonSocial = String(alumno?.razonSocial || "").trim();
  const fisica = [alumno?.nombre, alumno?.apellidoPaterno, alumno?.apellidoMaterno]
    .filter(Boolean)
    .join(" ")
    .trim();
  return (alumno?.persona ? razonSocial || fisica : fisica || razonSocial) || "";
};

//El código va al correo del archivo Y a los que ya tiene el alumno en su ficha, con el del
//archivo primero. Mismo criterio que `armarDestinatarios` del backend.
export const destinatariosDe = (correoArchivo, alumno) => {
  const vistos = new Set();
  const limpios = [];
  [correoArchivo, alumno?.correo, ...(alumno?.correosAdicionales || [])].forEach(
    (valueCorreo) => {
      const correo = String(valueCorreo || "").trim();
      if (!correo) return;
      const llave = correo.toLowerCase();
      if (vistos.has(llave)) return;
      vistos.add(llave);
      limpios.push(correo);
    },
  );
  return limpios;
};

export const esCorreoNuevo = (correoArchivo, alumno) => {
  const correo = String(correoArchivo || "").trim().toLowerCase();
  if (!correo) return false;
  return ![alumno?.correo, ...(alumno?.correosAdicionales || [])].some(
    (valueCorreo) => String(valueCorreo || "").trim().toLowerCase() === correo,
  );
};
